import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { UserPlus, Trash2, Loader2, Shield, User as UserIcon } from "lucide-react";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Backoffice() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "admin" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success("Utilizador criado");
      setForm({ email: "", name: "", password: "", role: "admin" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utilizador eliminado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Utilizadores</h1>
          <p className="text-sm text-muted-foreground mt-1">Crie e remova utilizadores com acesso ao CRM.</p>
        </div>

        <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={create} className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="backoffice-create-form">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            <h2 className="font-display text-base font-bold tracking-tight">Novo utilizador</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="bo-email">Email</Label>
              <Input id="bo-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="nome@mundideias.pt" data-testid="backoffice-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bo-name">Nome</Label>
              <Input id="bo-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome (opcional)" data-testid="backoffice-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bo-password">Palavra-passe</Label>
              <Input id="bo-password" type="password" required minLength={6} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••" data-testid="backoffice-password-input" />
            </div>
            <div className="space-y-2">
              <Label>Função</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="backoffice-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="superadmin">Super-administrador</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="user">Utilizador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={creating} className="gap-2" data-testid="backoffice-create-button">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Criar utilizador
            </Button>
          </div>
        </motion.form>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/40">
            <span className="text-sm font-semibold">{users.length} utilizador(es)</span>
          </div>
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <ul className="divide-y divide-border">
              {users.map((u) => (
                <li key={u.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`user-row-${u.email}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${u.role !== "user" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                      {u.role !== "user" ? <Shield className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{u.name || u.email}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="outline" className={u.role !== "user" ? "border-primary/40 text-primary" : ""}>
                      {u.role === "superadmin" ? "Super-admin" : u.role === "admin" ? "Admin" : "Utilizador"}
                    </Badge>
                    {u.email === me?.email ? (
                      <span className="text-xs text-muted-foreground w-9 text-center">Você</span>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            data-testid={`delete-user-button-${u.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminar utilizador?</AlertDialogTitle>
                            <AlertDialogDescription>
                              A conta <strong>{u.email}</strong> deixará de ter acesso ao CRM. Esta ação é permanente.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(u.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              data-testid={`confirm-delete-user-${u.id}`}>
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
